import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { FaHeart, FaComment, FaShare, FaArrowLeft, FaLock, FaWallet } from 'react-icons/fa';
import toast from 'react-hot-toast';
import CommentSection from '../video/components/CommentSection';

interface Video {
  id: string;
  video_url: string;
  thumbnail_url: string;
  title: string;
  description: string;
  category: string;
  language: string;
  likes_count: number;
  comments_count: number;
  shares_count: number;
  is_paid: boolean;
  price: number;
  user_id: string;
  user: {
    email: string;
    profile_image: string | null;
  };
}

export default function VideoPost() {
  const { id } = useParams<{ id: string }>();
  const [video, setVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    loadVideo();
    if (user) {
      checkLikeStatus();
      checkPurchaseStatus();
      loadUserBalance();
    }
  }, [id]);

  const loadVideo = async () => {
    try {
      const { data, error } = await supabase
        .from('ponavideos')
        .select(`
          *,
          user:ponousers (
            email,
            profile_image
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setVideo(data);
    } catch (err) {
      console.error('Error loading video:', err);
      setError('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const loadUserBalance = async () => {
    try {
      const { data, error } = await supabase
        .from('ponousers')
        .select('wallet_balance')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      setUserBalance(data.wallet_balance || 0);
    } catch (err) {
      console.error('Error loading balance:', err);
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('ponovideoaccess')
        .select('id')
        .eq('video_id', id)
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      setHasPurchased(!!data);
    } catch (err) {
      console.error('Error checking purchase status:', err);
    }
  };

  const handlePurchase = async () => {
    if (!user || !video) return;

    try {
      setPurchasing(true);

      // Check if user has enough balance
      if (userBalance < video.price) {
        toast.error('Insufficient balance. Please add funds to your wallet.');
        return;
      }

      // Start transaction
      const newBalance = userBalance - video.price;

      // Update user's balance
      const { error: balanceError } = await supabase
        .from('ponousers')
        .update({ wallet_balance: newBalance })
        .eq('id', user.id);

      if (balanceError) throw balanceError;

      // Record the purchase
      const { error: accessError } = await supabase
        .from('ponovideoaccess')
        .insert({
          video_id: video.id,
          user_id: user.id,
          amount_paid: video.price
        });

      if (accessError) throw accessError;

      // Record the transaction
      const { error: transactionError } = await supabase
        .from('stripetransactions')
        .insert({
          user_id: user.id,
          amount: video.price,
          type: 'withdraw',
          status: 'completed',
          description: `Purchased video: ${video.title}`
        });

      if (transactionError) throw transactionError;

      setHasPurchased(true);
      setUserBalance(newBalance);
      toast.success('Video purchased successfully!');
    } catch (err) {
      console.error('Error purchasing video:', err);
      toast.error('Failed to purchase video');
    } finally {
      setPurchasing(false);
    }
  };

  const checkLikeStatus = async () => {
    try {
      const { data } = await supabase
        .from('video_likes')
        .select('id')
        .eq('video_id', id)
        .eq('user_id', user?.id)
        .maybeSingle();

      setIsLiked(!!data);
    } catch (err) {
      console.error('Error checking like status:', err);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please log in to like videos');
      return;
    }

    try {
      if (isLiked) {
        await supabase
          .from('video_likes')
          .delete()
          .eq('video_id', id)
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('video_likes')
          .insert({
            video_id: id,
            user_id: user.id
          });
      }

      setIsLiked(!isLiked);
      loadVideo();
    } catch (err) {
      console.error('Error toggling like:', err);
      toast.error('Failed to update like');
    }
  };

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/video/${id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');

      if (user) {
        await supabase
          .from('ponovideoshares')
          .insert({
            video_id: id,
            user_id: user.id,
            share_type: 'copy',
            share_url: shareUrl
          });

        loadVideo();
      }
    } catch (err) {
      console.error('Error sharing video:', err);
      toast.error('Failed to share video');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <p className="text-xl mb-4">{error || 'Video not found'}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-red-500 hover:text-red-400"
        >
          <FaArrowLeft className="mr-2" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  const canWatchVideo = !video.is_paid || hasPurchased || user?.is_admin;

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-red-500 hover:text-red-400 mb-6"
        >
          <FaArrowLeft className="mr-2" />
          Back to Dashboard
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player Section */}
          <div className="lg:col-span-2">
            <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
              {!canWatchVideo ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-75 text-white">
                  <FaLock className="w-12 h-12 mb-4" />
                  <p className="text-xl font-bold mb-2">Premium Content</p>
                  <p className="text-gray-400 mb-4">Purchase to unlock this video</p>
                  <p className="text-2xl font-bold text-red-500 mb-4">${video.price}</p>
                  <div className="flex items-center space-x-2 text-sm text-gray-400 mb-4">
                    <FaWallet className="w-4 h-4" />
                    <span>Your Balance: ${userBalance.toFixed(2)}</span>
                  </div>
                  <button
                    onClick={handlePurchase}
                    disabled={purchasing || userBalance < video.price}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {purchasing ? 'Processing...' : 'Purchase Now'}
                  </button>
                  {userBalance < video.price && (
                    <p className="mt-2 text-sm text-red-400">
                      Insufficient balance. Please add funds to your wallet.
                    </p>
                  )}
                </div>
              ) : (
                <video
                  src={video.video_url}
                  poster={video.thumbnail_url}
                  controls
                  className="w-full h-full"
                />
              )}
            </div>

            {/* Video Info */}
            <div className="mt-4">
              <h1 className="text-2xl font-bold text-white mb-2">{video.title}</h1>
              <div className="flex items-center justify-between text-gray-400 mb-4">
                <div className="flex items-center space-x-4">
                  <span>{video.category}</span>
                  <span>{video.language}</span>
                </div>
                <div className="flex items-center space-x-6">
                  <button
                    onClick={handleLike}
                    className={`flex items-center space-x-1 ${isLiked ? 'text-red-500' : 'hover:text-red-500'}`}
                  >
                    <FaHeart />
                    <span>{video.likes_count}</span>
                  </button>
                  <button
                    onClick={() => setShowComments(!showComments)}
                    className="flex items-center space-x-1 hover:text-white"
                  >
                    <FaComment />
                    <span>{video.comments_count}</span>
                  </button>
                  <button
                    onClick={handleShare}
                    className="flex items-center space-x-1 hover:text-white"
                  >
                    <FaShare />
                    <span>{video.shares_count}</span>
                  </button>
                </div>
              </div>
              <p className="text-gray-300">{video.description}</p>
            </div>
          </div>

          {/* Comments Section */}
          <div className={`lg:col-span-1 ${showComments ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-gray-900 rounded-lg h-full overflow-hidden">
              <CommentSection
                videoId={video.id}
                isVisible={true}
                onCommentAdded={loadVideo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}